import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import apiClient from '../../lib/api';
import { LoadingSpinner } from '../../components/Loading';
import { Button } from '@/components/Button';
import { Input } from '@/components/Input';

export function ForgotPassword() {
  const [email, setEmail] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isSent, setIsSent] = useState(false);
  const navigate = useNavigate();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email) {
      toast.error('Please enter your email address');
      return;
    }

    setIsLoading(true);
    try {
      await apiClient.post('/auth/forgot-password', { email });
      setIsSent(true);
      toast.success('Password reset link sent! Check your email.');
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to send reset link');
    } finally {
      setIsLoading(false);
    }
  };

  if (isSent) {
    return (
       <div className="max-w-md mx-auto rounded-lg border border-border bg-card p-6 shadow-lg sm:p-8">
        <div className="text-center">
          <div className="text-5xl mb-4"></div>
          <h2 className="text-foreground tracking-tight font-display text-section-title">Check Your Email</h2>
          <p className="text-xs font-semibold text-muted-foreground mt-2">
            We've sent a password reset link to <strong className="text-foreground">{email}</strong>
          </p>
          <p className="text-xs text-muted-foreground mt-4 leading-relaxed">
            Didn't receive the email? Check your spam folder or click below to send another.
          </p>
        </div>
        <div className="space-y-3">
           <Button variant="primary"
            onClick={() => setIsSent(false)}
             fullWidth
          >
            Send Another Link
          </Button>
           <Button variant="secondary"
            onClick={() => navigate('/login')}
             fullWidth
          >
            Back to Login
          </Button>
        </div>
      </div>
    );
  }

  return (
     <div className="max-w-md mx-auto rounded-lg border border-border bg-card p-6 shadow-lg sm:p-8">
      <div className="text-center">
        <div className="text-5xl mb-4"></div>
        <h2 className="text-foreground tracking-tight font-display text-section-title">Forgot Password</h2>
        <p className="text-xs font-semibold text-muted-foreground mt-1">Enter your email and we'll send you a reset link</p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-5">
        {/* Email Address Input (Floating Label) */}
        <div className="relative group">
          <span className="absolute inset-y-0 left-0 pl-3.5 flex items-center text-muted-foreground pointer-events-none transition-colors group-focus-within:text-primary">
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 12a4 4 0 10-8 0 4 4 0 008 0zm0 0v1.5a2.5 2.5 0 005 0V12a9 9 0 10-9 9m4.5-1.206a8.959 8.959 0 01-4.5 1.207" />
            </svg>
          </span>
          <Input
            id="email"
            name="email"
            type="email"
            placeholder=" "
            value={email}
            onChange={(e) => setEmail(e.target.value)}
           className="peer block w-full rounded-sm border border-input bg-secondary px-3 py-2 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-ring/30"
            required
          />
          <label
            htmlFor="email"
            className="absolute left-10 top-3.5 text-muted-foreground text-xs transition-all duration-200 pointer-events-none origin-[0] transform px-1
 peer-focus:-translate-y-6 peer-focus:left-3 peer-focus:scale-[0.8] peer-focus:text-primary peer-focus:bg-card peer-focus:font-semibold
 peer-[:not(:placeholder-shown)]:-translate-y-6 peer-[:not(:placeholder-shown)]:left-3 peer-[:not(:placeholder-shown)]:scale-[0.8] peer-[:not(:placeholder-shown)]:bg-card peer-[:not(:placeholder-shown)]:font-semibold"
          >
            Email Address
          </label>
        </div>

        {/* Submit Button */}
         <Button variant="primary"
          type="submit"
          disabled={isLoading}
           fullWidth
        >
          {isLoading ? (
            <div className="flex items-center justify-center gap-2">
              <LoadingSpinner size="sm" />
              <span>Sending Reset Link...</span>
            </div>
          ) : (
            <span>Send Reset Link</span>
          )}
        </Button>
      </form>

      {/* Footer links */}
      <div className="text-center select-none text-xs font-semibold">
        <Link to="/login" className="text-muted-foreground hover:text-foreground transition-colors">
          &larr; Back to Login
        </Link>
      </div>

      <div className="pt-6 border-t border-border select-none text-xs font-semibold text-center">
        <p className="text-muted-foreground">
          Remember your password?{' '}
          <Link to="/login" className="text-primary hover:text-primary transition-colors font-semibold ml-1">
            Sign In
          </Link>
        </p>
      </div>
    </div>
  );
}
